import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { marked } from 'marked';
import { useAuth } from '@erpsquad/common/runtime';
import { getToken } from '../../utils';
import { getDefaultUserData } from '../../utils/common';
import { getApiConfigSafe } from '../../utils/api-config';
import { resetV1CopilotSession, startV1CopilotAgent, formAssistV1Copilot, streamV1CopilotChat, uploadDocument as apiUploadDocument, uploadDocuments as apiUploadDocuments, getDocumentStatus as apiGetDocumentStatus, setAiFeatureBaseUrl, getV1CopilotQuestions, postV1CopilotSubmit, postV1CopilotSaveDraft, postV1CopilotDiscard } from '../../api-client/api.ai-feature/api';
import { getV1PartiesId, getV1ChartOfAccountId, getV1PaymentTermsId, getV1CurrencyId, getV1TaxCodeId } from '../../api-client/api.accounting/api';
import { getV1CompanyId, getV1LocationsStates } from '../../api-client/api.rbac/api';
import { getV1InventoryItemsId, getV1Uom, getV1WarehouseLocationId } from '../../api-client/api.inventory/api';
import { getV1DepartmentsId } from '../../api-client/api.hrms/api';

import { IMessage, IMode, FormType, IDocument, getTime } from './types';
import { SLASH_COMMANDS } from './constants';
import ChatBotHeader from './chat-bot-header';
import ChatBotWelcome from './chat-bot-welcome';
import ChatBotMessages from './chat-bot-messages';
import ChatBotInput from './chat-bot-input';
import ChatBotLauncher from './chat-bot-launcher';
import InvoicePreview from './invoice-preview';
import DocUpload from './doc-upload';
import DocStatus from './doc-status';
import DocIntro from './doc-intro';
import toast from 'react-hot-toast';

import './chat-bot.scss';

marked.use({ breaks: true, gfm: true });

const ChatBot = () => {
	const isAiEnabled = getApiConfigSafe()?.aiFeatureEnabled === 'true';
	if (!isAiEnabled) return null;

	const uData = JSON.parse(localStorage.getItem('_u_data') || '{}');
	if (uData.role_type !== 'Admin') return null;

	const { user } = useAuth();
	const [isOpen, setIsOpen] = useState(false);
	const [messages, setMessages] = useState<IMessage[]>([]);
	const [isTyping, setIsTyping] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [mode, setMode] = useState<IMode>('conversation');
	const [formType, setFormType] = useState<FormType | null>(null);
	const [formStatus, setFormStatus] = useState<string>('in_progress');
	const [currentFieldUpdates, setCurrentFieldUpdates] = useState<Record<string, any> | null>(null);
	const [resolvedLabels, setResolvedLabels] = useState<Record<string, string>>({});
	const [dynamicQuestions, setDynamicQuestions] = useState<any[]>([]);
	const [showSlashMenu, setShowSlashMenu] = useState(false);
	const [slashIndex, setSlashIndex] = useState(0);
	const labelCacheRef = useRef<Record<string, string>>({});
	const bodyRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const userScrolledUp = useRef(false);
	const agentStarted = useRef(false);

	// Document state
	const [activeDocument, setActiveDocument] = useState<IDocument | null>(null);
	const [docIntroSent, setDocIntroSent] = useState(false);
	const [docError, setDocError] = useState<string | null>(null);

	// Multi-Document state
	const [activeDocuments, setActiveDocuments] = useState<IDocument[]>([]);
	const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
	const fetchedMultiDocIntroIds = useRef<string>('');

	const firstName = user?.first_name || '';

	const filteredMessages = useMemo(
		() => messages.filter(m => !m.mode || m.mode === mode),
		[messages, mode]
	);

	// Initialize the ai-feature base URL from config
	useEffect(() => {
		const config = getApiConfigSafe();
		if (config?.backendBaseUrl) {
			setAiFeatureBaseUrl(`${config.backendBaseUrl}/ai-feature`);
		}
	}, []);

	const slashQuery = inputValue.startsWith('/') ? inputValue.slice(1).toLowerCase() : '';
	const filteredCommands = SLASH_COMMANDS.filter((cmd) =>
		cmd.label.toLowerCase().startsWith(slashQuery)
	);

	const fetchQuestions = useCallback(async () => {
		try {
			const { 'x-token': token } = getToken();
			const uData = getDefaultUserData();
			const mapLocalToApiModule = (localName: string): string | null => {
				switch (localName) {
					case 'accounts-and-finance': return 'Accounting';
					case 'inventory': return 'Inventory';
					case 'manufacturing': return 'Manufacturing';
					case 'purchase': return 'Procurement';
					case 'sales': return 'CRM';
					case 'hrms': return 'HRMS';
					case 'rental': return 'Rental';
					case 'rbac':
					case 'user': return 'Users';
					default: return null;
				}
			};

			const mappedModules = (uData?.module_access || [])
				.map((m: any) => mapLocalToApiModule(m.module_name))
				.filter(Boolean) as string[];

			const uniqueModules = Array.from(new Set(mappedModules));
			if (uniqueModules.length === 0) {
				setDynamicQuestions([]);
				return;
			}

			const modules = [...uniqueModules].sort(() => Math.random() - 0.5).slice(0, 4);

			const res = await getV1CopilotQuestions(modules, token);
			if (res && res.modules) {
				const questions: any[] = [];
				res.modules.forEach((mod: any) => {
					if (mod.questions) {
						mod.questions.forEach((q: string) => {
							questions.push({
								prompt: q,
								cat: mod.module.toUpperCase(),
								mode: 'conversation',
								kind: 'blue'
							});
						});
					}
				});
				setDynamicQuestions(questions);
			}
		} catch (e) {
			console.error('Failed to fetch copilot questions', e);
		}
	}, []);

	useEffect(() => {
		if (isOpen) {
			setTimeout(() => textareaRef.current?.focus(), 100);
			if (dynamicQuestions.length === 0) {
				fetchQuestions();
			}
		}
	}, [isOpen, dynamicQuestions.length, fetchQuestions]);

	useEffect(() => {
		const container = bodyRef.current;
		if (!container) return;
		const onScroll = () => {
			const atBottom =
				container.scrollHeight - container.scrollTop - container.clientHeight < 40;
			userScrolledUp.current = !atBottom;
		};
		container.addEventListener('scroll', onScroll);
		return () => container.removeEventListener('scroll', onScroll);
	}, []);

	useEffect(() => {
		if (!userScrolledUp.current && bodyRef.current) {
			bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
		}
	}, [messages, isTyping, currentFieldUpdates, resolvedLabels, activeDocument, activeDocuments, suggestedQuestions]);

	const selectCommand = (id: IMode) => {
		setMode(id);
		setShowSlashMenu(false);
		setSlashIndex(0);
		if (id === 'conversation') {
			setFormType(null);
			setFormStatus('in_progress');
			agentStarted.current = false;
		} else if (id === 'agent' && !formType) {
			setFormType('sales_invoice');
		}
		setTimeout(() => textareaRef.current?.focus(), 0);
	};

	const handleModeChange = (id: IMode) => {
		selectCommand(id);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const val = e.target.value;
		setInputValue(val);
		if (val.startsWith('/')) {
			setShowSlashMenu(true);
			setSlashIndex(0);
		} else {
			setShowSlashMenu(false);
		}
	};

	const handleSuggestion = (prompt: string, suggestionMode: IMode, suggestionFormType?: FormType) => {
		setMode(suggestionMode);
		if (suggestionFormType) setFormType(suggestionFormType);
		setInputValue(prompt);
		setTimeout(() => {
			const ta = textareaRef.current;
			if (ta) {
				ta.focus();
				ta.selectionStart = ta.selectionEnd = prompt.length;
			}
		}, 0);
	};

	// ── Document upload handlers ──────────────────────────────────────────────
	const handleDocUpload = useCallback(async (file: File, onProgress: (pct: number) => void): Promise<IDocument> => {
		const { 'x-token': token } = getToken();
		setDocError(null);
		const result = await apiUploadDocument(file, token, undefined, onProgress);
		const doc: IDocument = {
			doc_id: result.doc_id,
			filename: result.filename,
			status: result.status as IDocument['status'],
			pages: result.pages,
		};
		setActiveDocument(doc);
		if (result.deduplicated && result.status === 'ready') {
			const fullStatus = await apiGetDocumentStatus(result.doc_id, token);
			const fullDoc: IDocument = {
				doc_id: fullStatus.doc_id,
				filename: fullStatus.filename || result.filename,
				status: fullStatus.status,
				pages: fullStatus.pages || result.pages,
				category: fullStatus.category || undefined,
				sub_category: fullStatus.sub_category || undefined,
				chunk_count: fullStatus.chunk_count || undefined,
			};
			setActiveDocument(fullDoc);
			setDocIntroSent(false);
			streamV1CopilotChat(
				{ ...getToken(), query: '', doc_id: fullDoc.doc_id, doc_intro: true },
				() => {}, () => {}, () => {}, () => {},
				(data: any) => {
					if (data?.found) {
						setActiveDocument((prev) => prev && prev.doc_id === fullDoc.doc_id ? {
							...prev,
							summary: data.summary,
							suggested_questions: data.suggested_questions,
							category: data.category || prev.category,
							sub_category: data.sub_category || prev.sub_category,
						} : prev);
					}
				},
			).catch(() => {});
			return fullDoc;
		}
		return doc;
	}, []);

	const handleDocUploadComplete = useCallback((doc: IDocument) => {
		setDocIntroSent(false);
	}, []);

	const handleDocUploadError = useCallback((msg: string) => {
		setDocError(msg);
	}, []);

	const handleDocReady = useCallback((doc: IDocument) => {
		setActiveDocument(doc);
		setDocIntroSent(false);
		const onDocIntro = (data: any) => {
			if (data?.found) {
				setActiveDocument((prev) => prev && prev.doc_id === doc.doc_id ? {
					...prev,
					summary: data.summary,
					suggested_questions: data.suggested_questions,
					category: data.category || prev.category,
					sub_category: data.sub_category || prev.sub_category,
				} : prev);
			}
		};
		streamV1CopilotChat(
			{ ...getToken(), query: '', doc_id: doc.doc_id, doc_intro: true },
			() => {}, () => {}, () => {}, () => {},
			onDocIntro,
		).catch(() => {});
	}, []);

	const handleDocError = useCallback((doc: IDocument) => {
		setActiveDocument(null);
		setDocError(doc.error || 'Document processing failed');
	}, []);

	const handleDocDelete = useCallback(async () => {
		if (!activeDocument) return;
		const docId = activeDocument.doc_id;
		try {
			const { 'x-token': token } = getToken();
			const { deleteDocument } = await import('../../api-client/api.ai-feature/api');
			await deleteDocument(docId, token);
			setActiveDocument((current) =>
				current?.doc_id === docId ? null : current
			);
			setDocIntroSent(false);
			setDocError(null);
		} catch {
			setDocError('Failed to delete document. Please try again.');
		}
	}, [activeDocument]);

	// ── Multi-Document upload handlers ──────────────────────────────────────────────
	const checkAndSendMultiDocIntro = useCallback((docs: IDocument[]) => {
		if (docs.length === 0) return;
		if (docs.some(d => d.status !== 'ready')) return;
		
		const currentIds = docs.map(d => d.doc_id).sort().join(',');
		if (fetchedMultiDocIntroIds.current === currentIds) return;
		fetchedMultiDocIntroIds.current = currentIds;

		streamV1CopilotChat(
			{ ...getToken(), query: '', doc_ids: docs.map(d => d.doc_id), doc_intro: true },
			() => {}, () => {}, () => {}, () => {},
			(data: any) => {
				if (data?.found) {
					if (data.suggested_questions) {
						setSuggestedQuestions(data.suggested_questions);
					}
					setActiveDocuments((prev) => prev.map(p => {
						if (docs.length === 1) {
							return {
								...p,
								summary: data.summary,
								category: data.category || p.category,
								sub_category: data.sub_category || p.sub_category,
							};
						} else if (data.docs && Array.isArray(data.docs)) {
							const dData = data.docs.find((d: any) => d.doc_id === p.doc_id);
							if (dData) {
								return {
									...p,
									summary: dData.summary,
									category: dData.category || p.category,
									sub_category: dData.sub_category || p.sub_category,
								};
							}
						}
						return p;
					}));
				}
			},
		).catch(() => {});
	}, [docIntroSent]);

	const handleMultiDocUpload = useCallback(async (files: File[], onProgress: (pct: number) => void): Promise<IDocument[]> => {
		const { 'x-token': token } = getToken();
		setDocError(null);
		const result = await apiUploadDocuments(files, token, undefined, onProgress);
		const items = Array.isArray(result) ? result : (result as any).items || [];
		
		const docs: IDocument[] = items.map((r: any) => ({
			doc_id: r.doc_id,
			filename: r.filename,
			status: r.status as IDocument['status'],
			pages: r.pages,
			error: r.error
		}));

		setActiveDocuments(prev => {
			const newDocs = [...prev];
			docs.forEach(d => {
				if (!newDocs.find(x => x.doc_id === d.doc_id)) newDocs.push(d);
			});
			return newDocs;
		});
		setDocIntroSent(false);

		const readyDocs = items.filter((r: any) => r.deduplicated && r.status === 'ready');
		if (readyDocs.length > 0) {
			const fullStatuses = await Promise.all(readyDocs.map((r: any) => apiGetDocumentStatus(r.doc_id, token)));
			setActiveDocuments(prev => {
				const updated = prev.map(p => {
					const full = fullStatuses.find(fs => fs.doc_id === p.doc_id);
					if (full) {
						return {
							...p,
							filename: full.filename || p.filename,
							status: full.status,
							pages: full.pages || p.pages,
							category: full.category || p.category,
							sub_category: full.sub_category || p.sub_category,
							chunk_count: full.chunk_count || p.chunk_count,
						};
					}
					return p;
				});
				setTimeout(() => checkAndSendMultiDocIntro(updated), 0);
				return updated;
			});
		}

		return docs;
	}, [checkAndSendMultiDocIntro]);

	const handleMultiDocUploadComplete = useCallback((docs: IDocument[]) => {
		setDocIntroSent(false);
	}, []);

	const handleMultiDocUploadError = useCallback((msg: string) => {
		setDocError(msg);
	}, []);

	const handleMultiDocReady = useCallback((doc: IDocument) => {
		setActiveDocuments(prev => {
			const updated = prev.map(p => p.doc_id === doc.doc_id ? doc : p);
			setTimeout(() => checkAndSendMultiDocIntro(updated), 0);
			return updated;
		});
	}, [checkAndSendMultiDocIntro]);

	const handleMultiDocError = useCallback((doc: IDocument) => {
		setActiveDocuments(prev => prev.filter(p => p.doc_id !== doc.doc_id));
		setDocError(doc.error || 'Document processing failed');
	}, []);

	const handleMultiDocDelete = useCallback(async (docId?: string) => {
		if (activeDocuments.length === 0) return;
		const docsToDelete = docId ? activeDocuments.filter(d => d.doc_id === docId) : activeDocuments;
		try {
			const { 'x-token': token } = getToken();
			const { deleteDocument } = await import('../../api-client/api.ai-feature/api');
			await Promise.all(docsToDelete.map(d => deleteDocument(d.doc_id, token)));
			setActiveDocuments((current) => docId ? current.filter(c => c.doc_id !== docId) : []);
			setDocIntroSent(false);
			setDocError(null);
			setSuggestedQuestions([]);
			fetchedMultiDocIntroIds.current = '';
		} catch {
			setDocError('Failed to delete document(s). Please try again.');
		}
	}, [activeDocuments]);

	const sendMessage = useCallback(
		async (text: string) => {
			if (isTyping) return;
			if (mode === 'conversation' && !text.trim()) return;
			if (mode === 'agent' && !formType) return;
			if (mode === 'doc_qa' && !activeDocument && activeDocuments.length === 0) return;

			const userText = text;

			setMessages((prev) => [
				...prev,
				{ id: Date.now(), role: 'user', text: userText, mode }
			]);
			setInputValue('');
			setIsTyping(true);

			let aiMsgId: number | null = null;
			const startTime = Date.now();

			const appendToken = (chunk: string) => {
				if (aiMsgId === null) {
					aiMsgId = Date.now() + 1;
					const responseTime = `${((Date.now() - startTime) / 1000).toFixed(1)}s`;
					setIsTyping(false);
					setMessages((prev) => [
						...prev,
						{ id: aiMsgId!, role: 'ai', text: chunk, time: responseTime, mode }
					]);
				} else {
					setMessages((prev) =>
						prev.map((m) => (m.id === aiMsgId ? { ...m, text: m.text + chunk } : m))
					);
				}
			};

			const onDone = () => { setIsTyping(false); };
			const onError = (message?: string) => {
				setIsTyping(false);
				const errorText = message || 'Sorry, something went wrong. Please try again.';
				if (aiMsgId !== null) {
					setMessages((prev) => prev.map((m) =>
						m.id === aiMsgId
							? { ...m, text: m.text + '\n\n⚠️ ' + errorText }
							: m
					));
				} else {
					setMessages((prev) => [
						...prev,
						{ id: Date.now() + 1, role: 'ai', text: errorText, time: getTime(), mode }
					]);
				}
			};
			const onOffTopic = () => {
				setIsTyping(false);
				if (aiMsgId === null) {
					setMessages((prev) => [
						...prev,
						{ id: Date.now() + 1, role: 'ai', text: 'That question is outside the scope of ERP data.', time: getTime(), mode }
					]);
				}
			};

			const resolveFieldLabel = async (key: string, value: any): Promise<string> => {
				const cacheKey = `${key}:${value}`;
				if (labelCacheRef.current[cacheKey]) return labelCacheRef.current[cacheKey];
				let label = String(value ?? '');
				try {
					if (key === 'customer_id' || key === 'vendor_id') {
						const res = await getV1PartiesId({ ...getToken(), id: value });
						const p = (res as any)?.data?.parties?.party?.data || (res as any)?.data?.parties?.party || {};
						label = p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || label;
					} else if (key === 'payment_term_id') {
						const res = await getV1PaymentTermsId({ ...getToken(), id: value });
						const pt = (res as any)?.data?.payment_term?.payment_term?.data || {};
						label = pt.name || label;
					} else if (key === 'account_payable_id' || key === 'account_receivable_id') {
						const res = await getV1ChartOfAccountId({ ...getToken(), id: value });
						const c = (res as any)?.data?.chart_of_account?.account?.data || {};
						label = c.account_name || label;
					} else if (key === 'company_id') {
						const res = await getV1CompanyId({ ...getToken(), id: value });
						const c = (res as any)?.data?.company?.data || {};
						label = c.company_name || label;
					} else if (key === 'currency_id' || key === 'supplier_currency_id') {
						const res = await getV1CurrencyId({ ...getToken(), id: value });
						const c = (res as any)?.data?.currency?.data || {};
						label = c.currency_name || label;
					} else if (key === 'region_id') {
						const res = await getV1LocationsStates({ ...getToken(), id: Number(value) });
						const r = (res as any)?.data?.state || res?.data || {}
						label = r.name || r.state_name || label;
					} else if (key === 'location' || key.startsWith('invoice_addresses.shipping_address') || key.startsWith('invoice_addresses.billing_address') || key.startsWith('invoice_addresses.place_of_supply')) {
						const res = await getV1WarehouseLocationId({ ...getToken(), id: value });
						const wl = (res as any)?.data?.warehouse_location?.warehouse_location?.data || {};
						label = wl.name || label;
					} else if (key === 'department' || key === 'department_id') {
						const res = await getV1DepartmentsId({ ...getToken(), id: value });
						const d = (res as any)?.data?.department?.department?.data || {};
						label = d.name || label;
					} else if (key.startsWith('invoice_addresses.supplier_address') || key.startsWith('invoice_addresses.contact_person')) {
						const res = await getV1PartiesId({ ...getToken(), id: value });
						const p = (res as any)?.data?.parties?.party?.data || (res as any)?.data?.parties?.party || {};
						label = p.company_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || label;
					}
				} catch {}
				labelCacheRef.current[cacheKey] = label;
				return label;
			};

			const onFieldUpdates = async (updates: Record<string, unknown>) => {
				setCurrentFieldUpdates(updates);
				const newLabels: Record<string, string> = {};

				const expenseAccountIds = updates['purchase_expense_entries.accounts.account_name'];
				if (Array.isArray(expenseAccountIds)) {
					const accountPromises = expenseAccountIds.map(async (accountId: string) => {
						const cacheKey = `purchase_expense_entries.accounts.account_name:${accountId}`;
						if (labelCacheRef.current[cacheKey]) {
							newLabels[cacheKey] = labelCacheRef.current[cacheKey];
							return;
						}
						try {
							const res = await getV1ChartOfAccountId({ ...getToken(), id: accountId });
							const c = (res as any)?.data?.chart_of_account?.account?.data || {};
							const label = c.account_name || `Account #${accountId}`;
							labelCacheRef.current[cacheKey] = label;
							newLabels[cacheKey] = label;
						} catch {
							newLabels[cacheKey] = `Account #${accountId}`;
						}
					});
					await Promise.all(accountPromises);
				}

				const expenseTaxCodes = updates['purchase_expense_entries.tax_codes'];
				if (Array.isArray(expenseTaxCodes)) {
					const uniqueTaxIds = [...new Set(expenseTaxCodes.filter(Boolean))];
					const taxPromises = uniqueTaxIds.map(async (taxId: string) => {
						const cacheKey = `tax_code_data:${taxId}`;
						if (labelCacheRef.current[cacheKey]) {
							newLabels[cacheKey] = labelCacheRef.current[cacheKey];
							return;
						}
						try {
							const res = await getV1TaxCodeId({ ...getToken(), id: taxId });
							const taxCode = (res as any)?.data?.taxCode?.data || {};
							const taxRate = Number(taxCode.rate) || 0;
							const taxName = taxCode.name || `Tax #${taxId}`;
							const label = JSON.stringify({ name: `${taxName} (${taxRate}%)`, rate: taxRate });
							labelCacheRef.current[cacheKey] = label;
							newLabels[cacheKey] = label;
						} catch {
							newLabels[cacheKey] = JSON.stringify({ name: `Tax #${taxId}`, rate: 0 });
						}
					});
					await Promise.all(taxPromises);
				}

				const itemNames = updates['sales_item_entries.items.name'] || updates['purchase_item_entries.items.name'];
				const itemPrefix = updates['sales_item_entries.items.name'] ? 'sales_item_entries' : 'purchase_item_entries';
				if (Array.isArray(itemNames)) {
					const itemPromises = itemNames.map(async (itemId: string) => {
						const cacheKey = `${itemPrefix}.items.name:${itemId}`;
						if (labelCacheRef.current[cacheKey]) {
							newLabels[cacheKey] = labelCacheRef.current[cacheKey];
							return;
						}
						try {
							const res = await getV1InventoryItemsId({ ...getToken(), id: itemId });
							const item = (res as any)?.data?.data || {};
							const label = item.name || `Item #${itemId}`;
							labelCacheRef.current[cacheKey] = label;
							newLabels[cacheKey] = label;
						} catch {
							newLabels[cacheKey] = `Item #${itemId}`;
						}
					});
					await Promise.all(itemPromises);
				}

				const taxCodes = updates['sales_item_entries.tax_codes'] || updates['purchase_item_entries.tax_codes'];
				if (Array.isArray(taxCodes)) {
					const uniqueTaxIds = [...new Set(taxCodes.filter(Boolean))];
					const taxPromises = uniqueTaxIds.map(async (taxId: string) => {
						const cacheKey = `tax_code_data:${taxId}`;
						if (labelCacheRef.current[cacheKey]) {
							newLabels[cacheKey] = labelCacheRef.current[cacheKey];
							return;
						}
						try {
							const res = await getV1TaxCodeId({ ...getToken(), id: taxId });
							const taxCode = (res as any)?.data?.taxCode?.data || {};
							const taxRate = Number(taxCode.rate) || 0;
							const taxName = taxCode.name || `Tax #${taxId}`;
							const label = JSON.stringify({ name: `${taxName} (${taxRate}%)`, rate: taxRate });
							labelCacheRef.current[cacheKey] = label;
							newLabels[cacheKey] = label;
						} catch {
							newLabels[cacheKey] = JSON.stringify({ name: `Tax #${taxId}`, rate: 0 });
						}
					});
					await Promise.all(taxPromises);
				}

				const uomKeys = ['sales_item_entries.units_of_measurement.uom_name', 'sales_item_entries.units_of_measurement.name', 'purchase_item_entries.units_of_measurement.uom_name'];
				const uomIdsForKey = uomKeys.find(k => Array.isArray(updates[k]));
				if (uomIdsForKey) {
					const uomIds = updates[uomIdsForKey] as string[];
					const uomPrefix = uomIdsForKey.startsWith('sales') ? 'sales_item_entries' : 'purchase_item_entries';
					const uomPromises = uomIds.map(async (uomId: string) => {
						const cacheKey = `${uomPrefix}.units_of_measurement:${uomId}`;
						if (labelCacheRef.current[cacheKey]) {
							newLabels[cacheKey] = labelCacheRef.current[cacheKey];
							return;
						}
						try {
							const res = await getV1Uom({ ...getToken(), filters: JSON.stringify({ id: uomId }) });
							const uomList = (res as any)?.data?.['uom'] || (res as any)?.data?.data || [];
							const uom = Array.isArray(uomList) ? uomList[0] : uomList;
							const label = uom?.name || uom?.uom_name || `UOM #${uomId}`;
							labelCacheRef.current[cacheKey] = label;
							newLabels[cacheKey] = label;
						} catch {
							newLabels[cacheKey] = `UOM #${uomId}`;
						}
					});
					await Promise.all(uomPromises);
				}

				const promises = Object.entries(updates).map(async ([key, value]) => {
					if (value !== undefined && value !== null && value !== '') {
						if (key === 'sales_item_entries.items.name' || key === 'purchase_item_entries.items.name' || key === 'purchase_expense_entries.accounts.account_name') return;
						if (uomKeys.includes(key)) return;
						newLabels[key] = await resolveFieldLabel(key, value);
					}
				});
				await Promise.all(promises);
				setResolvedLabels((prev) => ({ ...prev, ...newLabels }));
			};

			try {
				if (mode === 'doc_qa') {
					if (activeDocuments && activeDocuments.length > 0) {
						await streamV1CopilotChat(
							{ ...getToken(), query: text, doc_ids: activeDocuments.map(d => d.doc_id) },
							appendToken, onDone, onError, onOffTopic
						);
					} else if (activeDocument) {
						await streamV1CopilotChat(
							{ ...getToken(), query: text, doc_id: activeDocument.doc_id },
							appendToken, onDone, onError, onOffTopic
						);
					}
				} else if (mode === 'agent' && formType) {
					if (!agentStarted.current) {
						await startV1CopilotAgent(
							{ ...getToken(), form_type: formType },
							appendToken,
							() => { agentStarted.current = true; },
							onError,
							onOffTopic
						);
					}
				await formAssistV1Copilot(
					{ ...getToken(), message: text },
					appendToken, onDone, onError, onFieldUpdates,
					async (action) => {
						if (['save_draft', 'submit_form', 'discard_form'].includes(action)) {
							try {
								const token = getToken()['x-token'];
								const sessionId = 'current_session';
								
								if (action === 'submit_form') {
									await postV1CopilotSubmit(sessionId, token, formType as string, currentFieldUpdates);
									toast.success('Form submitted successfully!');
									setTimeout(() => setIsOpen(false), 2000);
								} else if (action === 'save_draft') {
									await postV1CopilotSaveDraft(sessionId, token, formType as string, currentFieldUpdates);
									toast.success('Draft saved successfully!');
									setTimeout(() => setIsOpen(false), 2000);
								} else if (action === 'discard_form') {
									await postV1CopilotDiscard(sessionId, token, formType as string, currentFieldUpdates);
									toast.success('Form discarded');
									setIsOpen(false);
								}
								
								// Only clean up state if the API action actually succeeded!
								agentStarted.current = false;
								setCurrentFieldUpdates(null);
								setResolvedLabels({});
								labelCacheRef.current = {};
								setFormStatus('in_progress');
								setMessages([]);
								
							} catch (e: any) {
								console.error('Failed to perform copilot action', e);
								let errMsg = 'Action failed. Please try again.';
								try {
									const parsed = JSON.parse(e.message);
									if (parsed.message) errMsg = parsed.message;
								} catch (err) {
									if (e.message) errMsg = e.message;
								}
								toast.error(errMsg);
								
								// Also display the error in the chat UI so the user doesn't miss it
								setMessages((prev) => [
									...prev,
									{ 
										id: Date.now(), 
										role: 'ai', 
										text: `Validation Error: ${errMsg}\n\nPlease correct the details and try again.`, 
										time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), 
										mode: 'agent' 
									}
								]);
							}
						}
					},
					(status) => {
						console.log("Copilot Form Status:", status);
						setFormStatus(status);
					}
				);
				} else {
					await streamV1CopilotChat(
						{ ...getToken(), query: text, 'x-timezone': new Date().getTimezoneOffset() },
						appendToken, onDone, onError, onOffTopic
					);
				}
			} catch {
				setIsTyping(false);
				if (aiMsgId === null) {
					setMessages((prev) => [
						...prev,
						{ id: Date.now() + 1, role: 'ai', text: 'Connection error. Please try again.', time: getTime(), mode }
					]);
				}
			}
		},
		[isTyping, mode, formType, activeDocument, activeDocuments]
	);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (showSlashMenu && filteredCommands.length > 0) {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setSlashIndex((i) => (i + 1) % filteredCommands.length);
				return;
			}
			if (e.key === 'ArrowUp') {
				e.preventDefault();
				setSlashIndex((i) => (i - 1 + filteredCommands.length) % filteredCommands.length);
				return;
			}
			if (e.key === 'Enter' || e.key === 'Tab') {
				e.preventDefault();
				const cmdId = filteredCommands[slashIndex].id;
				setInputValue('');
				selectCommand(cmdId);
				return;
			}
			if (e.key === 'Escape') {
				setShowSlashMenu(false);
				setInputValue('');
				return;
			}
		}
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage(inputValue);
		}
	};

	const handleSave = useCallback(async () => {
		await sendMessage('Save the draft');
	}, [sendMessage]);

	const handleDiscard = useCallback(async () => {
		await sendMessage('Discard this form');
	}, [sendMessage]);

	const handleSubmit = useCallback(async () => {
		await sendMessage('Submit this form');
	}, [sendMessage]);

	return (
		<>
			{isOpen && (
				<div className='chatBot--panel'>
					<ChatBotHeader
						onClose={() => setIsOpen(false)}
						onNewChat={async () => {
							const { 'x-token': token } = getToken();
							await resetV1CopilotSession(token);
							setIsTyping(false);
							setFormType(null);
							setFormStatus('in_progress');
							agentStarted.current = false;
							setCurrentFieldUpdates(null);
							setResolvedLabels({});
							labelCacheRef.current = {};
							setActiveDocument(null);
							setDocIntroSent(false);
							setDocError(null);
							setActiveDocuments([]);
							setSuggestedQuestions([]);
							setMessages([]);
							fetchedMultiDocIntroIds.current = '';
							fetchQuestions();
						}}
					/>

					{(filteredMessages.length === 0 && !activeDocument && activeDocuments.length === 0) ? (
						<div ref={bodyRef} className='chatBot--body'>
							<ChatBotWelcome firstName={firstName} onSuggestion={handleSuggestion} dynamicQuestions={dynamicQuestions} mode={mode} />
						</div>
					) : (
						<ChatBotMessages
							messages={filteredMessages}
							isTyping={isTyping}
							bodyRef={bodyRef}
							footer={
								<>
									{activeDocument && activeDocuments.length === 0 && mode === 'doc_qa' && activeDocument.status === 'ready' && !docIntroSent && filteredMessages.length === 0 && (
										<DocIntro
											document={activeDocument}
											onSuggestion={(q) => {
												setDocIntroSent(true);
												setInputValue(q);
												setTimeout(() => sendMessage(q), 0);
											}}
										/>
									)}
									{activeDocuments.length > 0 && mode === 'doc_qa' && activeDocuments.every(d => d.status === 'ready') && !docIntroSent && filteredMessages.length === 0 && (
										<DocIntro
											documents={activeDocuments}
											suggestedQuestions={suggestedQuestions.length > 0 ? suggestedQuestions : activeDocuments[0].suggested_questions}
											onSuggestion={(q) => {
												setDocIntroSent(true);
												setInputValue(q);
												setTimeout(() => sendMessage(q), 0);
											}}
										/>
									)}
									{currentFieldUpdates && mode === 'agent' && (
										<InvoicePreview
											fieldUpdates={currentFieldUpdates}
											resolvedLabels={resolvedLabels}
											formStatus={formStatus}
											onSave={handleSave}
											onDiscard={handleDiscard}
											onSubmit={handleSubmit}
										/>
									)}
								</>
							}
						/>
					)}

					{mode === 'doc_qa' && !activeDocument && activeDocuments.length === 0 && (
						<div className='chatBot--doc-upload-area'>
							<DocUpload
								onUploadComplete={(docs) => {
									if (docs.length === 1 && (!activeDocuments || activeDocuments.length === 0)) {
										handleDocUploadComplete(docs[0]);
									} else {
										handleMultiDocUploadComplete(docs);
									}
								}}
								onUploadError={handleDocUploadError}
								uploadDocument={handleDocUpload}
								uploadDocuments={handleMultiDocUpload}
							/>
							{docError && <div className='chatBot--doc-error'>{docError}</div>}
						</div>
					)}

					{activeDocument && activeDocuments.length === 0 && mode === 'doc_qa' && ['pending', 'processing', 'parsing', 'embedding', 'indexing'].includes(activeDocument.status) && (
						<div className='chatBot--doc-status-area'>
							<DocStatus key={activeDocument.doc_id}
								document={activeDocument}
								onReady={handleDocReady}
								onError={handleDocError}
								getStatus={async (docId) => {
									const { 'x-token': token } = getToken();
									const res = await apiGetDocumentStatus(docId, token);
									return {
										doc_id: res.doc_id,
										filename: res.filename || activeDocument.filename,
										status: res.status,
										pages: res.pages || activeDocument.pages,
										category: res.category || undefined,
										sub_category: res.sub_category || undefined,
										chunk_count: res.chunk_count || undefined,
										error: res.error || undefined,
										error_code: res.error_code || undefined,
									};
								}}
							/>
						</div>
					)}

					{mode === 'doc_qa' && activeDocuments.length > 0 && activeDocuments.some(d => ['pending', 'processing', 'parsing', 'embedding', 'indexing'].includes(d.status)) && (
						<div className='chatBot--doc-status-area chatBot--doc-status-area--multiple'>
							{activeDocuments.filter(d => ['pending', 'processing', 'parsing', 'embedding', 'indexing'].includes(d.status)).map(doc => (
								<DocStatus key={doc.doc_id}
									document={doc}
									onReady={handleMultiDocReady}
									onError={handleMultiDocError}
									getStatus={async (docId) => {
										const { 'x-token': token } = getToken();
										const res = await apiGetDocumentStatus(docId, token);
										return {
											doc_id: res.doc_id,
											filename: res.filename || doc.filename,
											status: res.status,
											pages: res.pages || doc.pages,
											category: res.category || undefined,
											sub_category: res.sub_category || undefined,
											chunk_count: res.chunk_count || undefined,
											error: res.error || undefined,
											error_code: res.error_code || undefined,
										};
									}}
								/>
							))}
						</div>
					)}

					{docError && mode === 'doc_qa' && !!(activeDocument || activeDocuments.length > 0) && (
						<div className='chatBot--doc-error-floating' onClick={() => setDocError(null)}>
							{docError}
						</div>
					)}

					<ChatBotInput
						inputValue={inputValue}
						isTyping={isTyping}
						mode={mode}
						formType={formType}
						showSlashMenu={showSlashMenu}
						filteredCommands={filteredCommands}
						slashIndex={slashIndex}
						textareaRef={textareaRef}
						onChange={handleInputChange}
						onKeyDown={handleKeyDown}
						onSend={() => sendMessage(inputValue)}
						onSlashHover={setSlashIndex}
						onSlashSelect={selectCommand}
						onModeChange={handleModeChange}
						onFormTypeChange={setFormType}
						activeDocument={activeDocument}
						activeDocuments={activeDocuments}
						onDocDelete={handleDocDelete}
						onDocUpload={() => {
							// don't clear the list necessarily
						}}
						onMultiDocDelete={handleMultiDocDelete}
					/>
				</div>
			)}

			{!isOpen && (
				<ChatBotLauncher onOpen={() => setIsOpen(true)} />
			)}
		</>
	);
};

export default ChatBot;
